import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, Building2Icon, StoreIcon, ActivityIcon, CreditCardIcon, DownloadIcon } from 'lucide-react';
import { formatMoney } from '../../../shared/lib/money';
import { outletsData, malls } from '../mocks/outletsData';
import { OutletDateRange } from '../components/OutletDateRange';
import { KpiCard } from '../../analytics/components/KpiCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
export function MallView() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    t
  } = useTranslation('common');
  // Reconstruct mall name from slug
  const mallName = malls.find(m => m.toLowerCase().replace(/ /g, '-') === id);
  if (!mallName) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center" data-id="element-7498">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-7499">Mall not found</h2>
        <button onClick={() => navigate('/outlets')} className="mt-4 text-blue-600 hover:text-blue-800" data-id="element-7500">
          Return to portfolio
        </button>
      </div>;
  }
  const mallOutlets = outletsData.filter(o => o.mall === mallName);
  const totalGmv = mallOutlets.reduce((acc, o) => acc + o.periodGmv, 0);
  const totalTxn = mallOutlets.reduce((acc, o) => acc + o.txnCount, 0);
  const activeOutlets = mallOutlets.filter(o => o.status === 'Active').length;
  const avgTicket = totalGmv / totalTxn;
  // Floor/Zone breakdown
  const zoneMap = new Map<string, number>();
  mallOutlets.forEach(o => {
    const current = zoneMap.get(o.floorZone) || 0;
    zoneMap.set(o.floorZone, current + o.periodGmv);
  });
  const zoneData = Array.from(zoneMap.entries()).map(([name, gmv]) => ({
    name,
    gmv
  })).sort((a, b) => b.gmv - a.gmv);
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-7501">
      {/* Breadcrumb & Header */}
      <div className="mb-8" data-id="element-7502">
        <nav className="flex text-sm text-gray-500 mb-4" aria-label="Breadcrumb" data-id="element-7503">
          <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-7504">
            <li className="inline-flex items-center" data-id="element-7505">
              <button onClick={() => navigate('/outlets')} className="hover:text-gray-900 transition-colors" data-id="element-7506">
                Operator
              </button>
            </li>
            <li data-id="element-7507">
              <div className="flex items-center" data-id="element-7508">
                <span className="mx-2 text-gray-400" data-id="element-7509">/</span>
                <span className="text-gray-900 font-medium" data-id="element-7510">{mallName}</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-7511">
          <div data-id="element-7512">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-7513">{mallName}</h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-7514">
              Property Overview • {mallOutlets.length} Total Outlets
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-7515">
            <OutletDateRange data-id="element-7516" />
            <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2" data-id="element-7517">
              <DownloadIcon size={16} data-id="element-7518" />
              {t('actions.export')}
            </button>
          </div>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-7519">
        <KpiCard title="Property GMV" value={formatMoney(totalGmv)} icon={<ActivityIcon size={20} data-id="element-7521" />} trend={{
        value: '8.5%',
        isPositive: true
      }} color="blue" data-id="element-7520" />
        <KpiCard title="Transactions" value={totalTxn.toLocaleString()} icon={<CreditCardIcon size={20} data-id="element-7523" />} trend={{
        value: '4.2%',
        isPositive: true
      }} color="green" data-id="element-7522" />
        <KpiCard title="Active Outlets" value={activeOutlets} icon={<StoreIcon size={20} data-id="element-7525" />} color="purple" data-id="element-7524" />
        <KpiCard title="Average Ticket" value={formatMoney(avgTicket)} icon={<Building2Icon size={20} data-id="element-7527" />} color="orange" data-id="element-7526" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-7528">
        {/* Floor/Zone Breakdown */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7529">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-7530">
            GMV by Floor / Zone
          </h2>
          <div className="h-80" data-id="element-7531">
            <ResponsiveContainer width="100%" height="100%" data-id="element-7532">
              <BarChart data={zoneData} layout="vertical" margin={{
              top: 5,
              right: 30,
              left: 40,
              bottom: 5
            }} data-id="element-7533">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" data-id="element-7534" />
                <XAxis type="number" tickFormatter={val => `RM${val / 1000}k`} stroke="#9CA3AF" data-id="element-7535" />
                <YAxis dataKey="name" type="category" stroke="#4B5563" fontSize={12} tickLine={false} axisLine={false} data-id="element-7536" />
                <Tooltip formatter={(value: number) => [formatMoney(value), 'GMV']} cursor={{
                fill: '#F3F4F6'
              }} contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} data-id="element-7537" />
                <Bar dataKey="gmv" fill="var(--brand-primary)" radius={[0, 4, 4, 0]} barSize={32} data-id="element-7538" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Outlets in Mall */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7539">
          <div className="flex items-center justify-between mb-6" data-id="element-7540">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-7541">Top Outlets</h2>
            <button onClick={() => navigate('/outlets/directory')} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-7542">
              View all
            </button>
          </div>
          <div className="space-y-4" data-id="element-7543">
            {mallOutlets.sort((a, b) => b.periodGmv - a.periodGmv).slice(0, 5).map(outlet => <div key={outlet.id} className="flex items-center justify-between cursor-pointer group" onClick={() => navigate(`/outlets/outlet/${outlet.id}`)} data-id="element-7544">
                  <div data-id="element-7545">
                    <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-7546">
                      {outlet.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-7547">
                      {outlet.floorZone} • {outlet.category}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-7548">
                    <p className="font-medium text-gray-900" data-id="element-7549">
                      {formatMoney(outlet.periodGmv)}
                    </p>
                  </div>
                </div>)}
          </div>
        </div>
      </div>
    </div>;
}